/**
 * Plan 41 — Family Auto-Order Subscription client API.
 *
 * TanStack Query hooks over `/api/family-subscriptions/*`. The PARENT
 * wizard + list page + lifecycle sheets consume these. The hooks
 * follow the canonical `apps/ordo/client/src/api/*.ts` shape:
 *
 *   - Pure `fetch*` helpers at the top of the file.
 *   - `use*Query` for reads with a 30s `staleTime`.
 *   - `use*Mutation` for writes; invalidate the list + detail keys on
 *     success so the next read pulls the fresh row without an
 *     optimistic-update reconciliation step.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Wire types — mirror `apps/ordo/server/src/routes/family-subscriptions.ts`
// ---------------------------------------------------------------------------

export type FamilyTier = 'ADVENTUROUS' | 'BALANCED' | 'PICKY'
import type { Mealtime } from '@/lib/mealtime'
export type { Mealtime }

/**
 * The mealtimes a family auto-order may be created for — mirrors
 * `parseMealtime` in `server/src/routes/family-subscriptions.ts`, which
 * rejects AM_SNACK / PM_SNACK even though the Prisma enum and the menus
 * endpoint both carry them. Pinned by
 * `client/test/api/family-subscription-mealtime-contract.test.ts`, because a
 * school CAN publish an AM-snack menu and the wizard must never offer a meal
 * the create call would 400 on.
 */
export const FAMILY_SUBSCRIPTION_MEALTIMES: readonly Mealtime[] = ['BREAKFAST', 'LUNCH', 'SNACK', 'SUPPER']
// ONE client-side definition — re-exported from the school API module and
// pinned against the Prisma enum by client/test/api/subscription-status-contract.test.ts.
import type { SubscriptionStatus } from '@/api/school-subscriptions'

export type { SubscriptionStatus }

export interface DayOfWeekPick {
  weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6
  bundleId: string | null
  letMenuDecide: boolean
  /**
   * PROD-1376 — how many of the meal this weekday orders ("3 slices").
   * Optional on the wire: rows written before 2026-09-24 carry none and
   * the server reads absent as 1. Multiplies the MEAL line only — the
   * side / beverage slots are subscription-wide toggles.
   */
  quantity?: number
}

/**
 * Ceiling the wizard's stepper enforces, mirroring the server's
 * `FAMILY_PICK_MAX_QUANTITY`. Duplicated rather than imported because the
 * client bundle does not take a dependency on @ordo/schema; the contract
 * test pins the two together.
 */
export const FAMILY_PICK_MAX_QUANTITY = 20

/**
 * Server-computed funding state for one auto-order — see
 * `server/src/managers/balance-autoreload-policy.ts`. Derived ONCE there so
 * Home, My Family, and Billing cannot disagree about whether an auto-order can
 * actually be paid for.
 *
 *   - `OK`                    — armed, exempt (INVOICED), or history.
 *   - `AT_RISK`               — still active; the next runner pass will skip
 *     and pause it because auto-reload cannot fire.
 *   - `PAUSED_BY_AUTO_RELOAD` — the runner already paused it; the audit trail
 *     names auto-reload.
 *   - `RESUME_BLOCKED`        — paused for another reason, and Resume stays
 *     refused until auto-reload is armed.
 */
export type AutoOrderAutoReloadState = 'OK' | 'AT_RISK' | 'PAUSED_BY_AUTO_RELOAD' | 'RESUME_BLOCKED'

/** Why auto-reload cannot fire. Drives which cause the alert copy names. */
export type AutoReloadDisarmedReason = 'DISABLED' | 'NO_CARD' | 'INCOMPLETE'

/** One auto-order named inside an alert. */
export interface AutoOrderAutoReloadIssue {
  id: string
  /** Parent-facing name — never a raw id. */
  label: string
  state: Exclude<AutoOrderAutoReloadState, 'OK'>
}

/**
 * One household account whose auto-reload can't fire while auto-orders are
 * still charged to it. `accountId` lets a per-account surface (the Billing
 * balance card renders one per household) show only its own.
 */
export interface AutoOrderAutoReloadAlert {
  accountId: string
  disarmedReason: AutoReloadDisarmedReason
  subscriptions: AutoOrderAutoReloadIssue[]
}

export interface FamilySubscriptionWire {
  id: string
  payerUserId: string
  studentUserId: string
  schoolId: string
  accountId: string
  mealtime: Mealtime
  tier: FamilyTier
  status: SubscriptionStatus
  rrule: string
  /** ISO-8601 string or null. */
  effectiveFrom: string | null
  effectiveTo: string | null
  pauseUntil: string | null
  isArchived: boolean
  displayName: string | null
  subscribeBundles: boolean
  subscribeSides: boolean
  subscribeBeverages: boolean
  picks: DayOfWeekPick[]
  favoriteIds: string[]
  dislikedIds: string[]
  skippedDates: string[]
  lifecycleEventCount: number
  createdAt: string
  updatedAt: string
  /**
   * Present on the LIST endpoint only (the detail page derives the same answer
   * from the account it already loads). Absent reads as `OK`.
   */
  autoReloadState?: AutoOrderAutoReloadState
}

export interface ListFamilySubscriptionsResponse {
  subscriptions: FamilySubscriptionWire[]
  /** Empty (or absent, pre-deploy) when nothing is wrong. */
  autoReloadAlerts?: AutoOrderAutoReloadAlert[]
}

export interface FamilySubscriptionDetailResponse {
  subscription: FamilySubscriptionWire
}

export interface FamilySubscriptionEvent {
  id: string
  subscriptionId: string
  action: 'CREATE' | 'UPDATE' | 'PAUSE' | 'RESUME' | 'SKIP' | 'UNSKIP' | 'CANCEL'
  params: Record<string, unknown>
  initiatorUserId: string
  reason: string | null
  occurredAt: string
}

export interface ListFamilySubscriptionEventsResponse {
  events: FamilySubscriptionEvent[]
}

/**
 * Pricing rework Phase E — one upcoming line off
 * `GET /api/family-subscriptions/:id/upcoming-prices`. The price is
 * resolved at asOf = `nextServiceDate` (never "now"); the UI labels it
 * "current price — subject to your school's pricing at time of order".
 */
export interface FamilySubscriptionUpcomingPriceLine {
  menuItemId: string
  itemName: string
  /** YMD of the next service date this line fires on. */
  nextServiceDate: string
  unitPriceCents: number
  /**
   * PROD-1376 — how many of this item that day's pick orders. Absent on a
   * pre-deploy server, which reads as 1.
   */
  quantity?: number
  currency: string
  /** False = the item is UNPRICED — render "Price not set", never $0.00. */
  isPriced: boolean
}

export interface FamilySubscriptionUpcomingPricesResponse {
  lines: FamilySubscriptionUpcomingPriceLine[]
}

export interface CreateFamilySubscriptionPayload {
  studentUserId: string
  schoolId: string
  accountId: string
  mealtime: Mealtime
  tier: FamilyTier
  subscribeBundles: boolean
  subscribeSides: boolean
  subscribeBeverages: boolean
  picks: DayOfWeekPick[]
  favoriteIds?: string[]
  dislikedIds?: string[]
  displayName?: string | null
  effectiveFrom?: string | null
  effectiveTo?: string | null
}

export interface UpdateFamilySubscriptionPayload {
  tier?: FamilyTier
  subscribeBundles?: boolean
  subscribeSides?: boolean
  subscribeBeverages?: boolean
  picks?: DayOfWeekPick[]
  favoriteIds?: string[]
  dislikedIds?: string[]
  displayName?: string | null
  effectiveFrom?: string | null
  effectiveTo?: string | null
}

export interface PauseFamilySubscriptionPayload {
  /** ISO-8601 string. Null = pause indefinitely. */
  pauseUntil: string | null
  reason?: string | null
}

export interface SkipFamilySubscriptionPayload {
  dateYmd: string
  reason?: string | null
}

export interface CancelFamilySubscriptionPayload {
  effective?: 'IMMEDIATE' | 'END_OF_WEEK'
  reason?: string | null
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

/**
 * `GET /api/family-subscriptions/offerings` — do the school's live menus for
 * this mealtime list a-la-carte sides / beverages? The wizard hides the
 * matching slot toggles when a class isn't offered (the server also rejects
 * enabling them, fail-closed). Breakfast and lunch can offer different
 * classes, so the answer is per-mealtime.
 */
export interface FamilyAlaCarteOfferingsResponse {
  offersAlaCarteSides: boolean
  offersAlaCarteBeverages: boolean
}

export async function fetchFamilyAlaCarteOfferings(
  schoolId: string,
  mealtime: Mealtime,
  signal?: AbortSignal,
): Promise<FamilyAlaCarteOfferingsResponse> {
  const params = new URLSearchParams({ schoolId, mealtime })
  return apiFetch<FamilyAlaCarteOfferingsResponse>(`/api/family-subscriptions/offerings?${params}`, { signal })
}

export function useFamilyAlaCarteOfferingsQuery(schoolId: string | null | undefined, mealtime: Mealtime) {
  return useQuery<FamilyAlaCarteOfferingsResponse>({
    queryKey: ['family-subscriptions', 'offerings', schoolId ?? '', mealtime],
    queryFn: ({ signal }) => fetchFamilyAlaCarteOfferings(schoolId!, mealtime, signal),
    enabled: Boolean(schoolId),
    staleTime: 60_000,
  })
}

/** Mirror of `GET /api/family-subscriptions/coverage` — the free-meal waiver read. */
export interface SubscriptionCoverageResponse {
  fullyCovered: boolean
  reason: string
}

export async function fetchSubscriptionCoverage(
  args: { studentUserId: string; mealtime: Mealtime; subscribeSides: boolean; subscribeBeverages: boolean },
  signal?: AbortSignal,
): Promise<SubscriptionCoverageResponse> {
  const params = new URLSearchParams({
    studentUserId: args.studentUserId,
    mealtime: args.mealtime,
    subscribeSides: String(args.subscribeSides),
    subscribeBeverages: String(args.subscribeBeverages),
  })
  return apiFetch<SubscriptionCoverageResponse>(`/api/family-subscriptions/coverage?${params}`, { signal })
}

/**
 * Free-meal coverage for the wizard's card-requirement gate. Display-only —
 * the server re-resolves on every create/update/resume, so this read can
 * never grant what the write path would refuse.
 */
export function useSubscriptionCoverageQuery(
  args: { studentUserId: string; mealtime: Mealtime; subscribeSides: boolean; subscribeBeverages: boolean } | null,
) {
  return useQuery<SubscriptionCoverageResponse>({
    queryKey: [
      'family-subscriptions',
      'coverage',
      args?.studentUserId ?? '',
      args?.mealtime ?? '',
      args?.subscribeSides ?? false,
      args?.subscribeBeverages ?? false,
    ],
    queryFn: ({ signal }) => fetchSubscriptionCoverage(args!, signal),
    enabled: Boolean(args?.studentUserId),
    staleTime: 60_000,
  })
}

export async function fetchFamilySubscriptions(signal?: AbortSignal): Promise<ListFamilySubscriptionsResponse> {
  return apiFetch<ListFamilySubscriptionsResponse>('/api/family-subscriptions', { signal })
}

export async function fetchFamilySubscription(
  id: string,
  signal?: AbortSignal,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}`, { signal })
}

export async function createFamilySubscription(
  payload: CreateFamilySubscriptionPayload,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>('/api/family-subscriptions', {
    method: 'POST',
    body: payload,
  })
}

export async function updateFamilySubscription(
  id: string,
  payload: UpdateFamilySubscriptionPayload,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: payload,
  })
}

export async function pauseFamilySubscription(
  id: string,
  payload: PauseFamilySubscriptionPayload,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}/pause`, {
    method: 'POST',
    body: payload,
  })
}

export async function resumeFamilySubscription(id: string): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}/resume`, {
    method: 'POST',
    body: {},
  })
}

export async function skipFamilySubscriptionDate(
  id: string,
  payload: SkipFamilySubscriptionPayload,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}/skip`, {
    method: 'POST',
    body: payload,
  })
}

export async function unskipFamilySubscriptionDate(
  id: string,
  dateYmd: string,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}/unskip`, {
    method: 'POST',
    body: { dateYmd },
  })
}

export async function cancelFamilySubscription(
  id: string,
  payload: CancelFamilySubscriptionPayload,
): Promise<FamilySubscriptionDetailResponse> {
  return apiFetch<FamilySubscriptionDetailResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}/cancel`, {
    method: 'POST',
    body: payload,
  })
}

export async function fetchFamilySubscriptionEvents(
  id: string,
  signal?: AbortSignal,
): Promise<ListFamilySubscriptionEventsResponse> {
  return apiFetch<ListFamilySubscriptionEventsResponse>(`/api/family-subscriptions/${encodeURIComponent(id)}/events`, {
    signal,
  })
}

export async function fetchFamilySubscriptionUpcomingPrices(
  id: string,
  signal?: AbortSignal,
): Promise<FamilySubscriptionUpcomingPricesResponse> {
  return apiFetch<FamilySubscriptionUpcomingPricesResponse>(
    `/api/family-subscriptions/${encodeURIComponent(id)}/upcoming-prices`,
    { signal },
  )
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const familySubscriptionsQueryKey = ['family-subscriptions'] as const
export const familySubscriptionQueryKey = (id: string) => ['family-subscriptions', id] as const
export const familySubscriptionEventsQueryKey = (id: string) => ['family-subscriptions', id, 'events'] as const
export const familySubscriptionUpcomingPricesQueryKey = (id: string) =>
  ['family-subscriptions', id, 'upcoming-prices'] as const

// ---------------------------------------------------------------------------
// TanStack Query hooks
// ---------------------------------------------------------------------------

export function useFamilySubscriptionsQuery() {
  return useQuery<ListFamilySubscriptionsResponse>({
    queryKey: familySubscriptionsQueryKey,
    queryFn: ({ signal }) => fetchFamilySubscriptions(signal),
    staleTime: 30_000,
  })
}

export function useFamilySubscriptionQuery(id: string | null | undefined) {
  return useQuery<FamilySubscriptionDetailResponse>({
    queryKey: familySubscriptionQueryKey(id ?? ''),
    queryFn: ({ signal }) => fetchFamilySubscription(id!, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

export function useFamilySubscriptionEventsQuery(id: string | null | undefined) {
  return useQuery<ListFamilySubscriptionEventsResponse>({
    queryKey: familySubscriptionEventsQueryKey(id ?? ''),
    queryFn: ({ signal }) => fetchFamilySubscriptionEvents(id!, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

export function useFamilySubscriptionUpcomingPricesQuery(id: string | null | undefined) {
  return useQuery<FamilySubscriptionUpcomingPricesResponse>({
    queryKey: familySubscriptionUpcomingPricesQueryKey(id ?? ''),
    queryFn: ({ signal }) => fetchFamilySubscriptionUpcomingPrices(id!, signal),
    enabled: Boolean(id),
    staleTime: 30_000,
  })
}

export function useCreateFamilySubscriptionMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateFamilySubscriptionPayload) => createFamilySubscription(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
    },
  })
}

export function useUpdateFamilySubscriptionMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateFamilySubscriptionPayload) => updateFamilySubscription(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
      void qc.invalidateQueries({ queryKey: familySubscriptionQueryKey(id) })
    },
  })
}

export function usePauseFamilySubscriptionMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: PauseFamilySubscriptionPayload) => pauseFamilySubscription(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
      void qc.invalidateQueries({ queryKey: familySubscriptionQueryKey(id) })
      void qc.invalidateQueries({ queryKey: familySubscriptionEventsQueryKey(id) })
    },
  })
}

export function useResumeFamilySubscriptionMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => resumeFamilySubscription(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
      void qc.invalidateQueries({ queryKey: familySubscriptionQueryKey(id) })
      void qc.invalidateQueries({ queryKey: familySubscriptionEventsQueryKey(id) })
    },
  })
}

export function useSkipFamilySubscriptionMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: SkipFamilySubscriptionPayload) => skipFamilySubscriptionDate(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
      void qc.invalidateQueries({ queryKey: familySubscriptionQueryKey(id) })
      void qc.invalidateQueries({ queryKey: familySubscriptionEventsQueryKey(id) })
    },
  })
}

export function useUnskipFamilySubscriptionMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (dateYmd: string) => unskipFamilySubscriptionDate(id, dateYmd),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
      void qc.invalidateQueries({ queryKey: familySubscriptionQueryKey(id) })
      void qc.invalidateQueries({ queryKey: familySubscriptionEventsQueryKey(id) })
    },
  })
}

export function useCancelFamilySubscriptionMutation(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (payload: CancelFamilySubscriptionPayload) => cancelFamilySubscription(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: familySubscriptionsQueryKey })
      void qc.invalidateQueries({ queryKey: familySubscriptionQueryKey(id) })
      void qc.invalidateQueries({ queryKey: familySubscriptionEventsQueryKey(id) })
    },
  })
}

// ---------------------------------------------------------------------------
// Pure UI helpers — exported for tests + the wizard
// ---------------------------------------------------------------------------

/**
 * The set of canonical school-week weekdays — Mon→Fri. Used by the
 * wizard's day-of-week picker. The Plan 41 mockups + Plan 06's
 * student-card use the same Mon-Fri vocabulary; weekends are rare
 * cases that v1 doesn't surface in the UI (RRULE supports them; the
 * default toggle is just Mon-Fri).
 */
export const SCHOOL_WEEK_WEEKDAYS: ReadonlyArray<0 | 1 | 2 | 3 | 4 | 5 | 6> = [1, 2, 3, 4, 5]

/**
 * Human label for a weekday — used by the wizard's day-toggle list.
 */
export function weekdayLabel(weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6): string {
  const labels: Record<0 | 1 | 2 | 3 | 4 | 5 | 6, string> = {
    0: 'Sun',
    1: 'Mon',
    2: 'Tue',
    3: 'Wed',
    4: 'Thu',
    5: 'Fri',
    6: 'Sat',
  }
  return labels[weekday]
}

/**
 * Long-form weekday label — "Monday" / "Tuesday" / etc. Used by the
 * review step where space allows the longer name.
 */
export function weekdayLongLabel(weekday: 0 | 1 | 2 | 3 | 4 | 5 | 6): string {
  const labels: Record<0 | 1 | 2 | 3 | 4 | 5 | 6, string> = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
  }
  return labels[weekday]
}

/**
 * Compress a sequence of weekdays into a short summary string — e.g.
 * `Mon, Tue, Wed, Thu, Fri` or `Mon, Wed, Fri`. Used by the student-card
 * subscription row + the list-page chip.
 */
export function summarizeWeekdays(picks: ReadonlyArray<DayOfWeekPick>): string {
  const weekdays = Array.from(new Set(picks.map((p) => p.weekday))).sort((a, b) => a - b)
  if (weekdays.length === 0) {
    return 'No delivery days'
  }
  // PROD-1376 — a day ordering more than one meal is named with its count
  // ("Wed x3"), because the compressed "Mon-Fri" form would otherwise hide
  // what the household is charged for. Every-day-is-one keeps the old copy.
  const quantityByWeekday = new Map(picks.map((p) => [p.weekday, p.quantity ?? 1]))
  const label = (w: 0 | 1 | 2 | 3 | 4 | 5 | 6): string => {
    const quantity = quantityByWeekday.get(w) ?? 1
    return quantity > 1 ? `${weekdayLabel(w)} x${quantity}` : weekdayLabel(w)
  }
  const allSingle = weekdays.every((w) => (quantityByWeekday.get(w) ?? 1) === 1)
  // Compress full school week to "Mon-Fri" for the common case.
  if (
    allSingle &&
    weekdays.length === 5 &&
    [1, 2, 3, 4, 5].every((w) => weekdays.includes(w as 0 | 1 | 2 | 3 | 4 | 5 | 6))
  ) {
    return 'Mon-Fri'
  }
  return weekdays.map((w) => label(w as 0 | 1 | 2 | 3 | 4 | 5 | 6)).join(', ')
}

/**
 * Tier short description — used by the wizard's tier picker + the
 * subscription card.
 */
export function tierDescription(tier: FamilyTier): { label: string; blurb: string } {
  if (tier === 'ADVENTUROUS') {
    return {
      label: 'Adventurous',
      blurb: 'Trust the chef — Ordo picks any meal on the menu.',
    }
  }
  if (tier === 'PICKY') {
    return {
      label: 'Picky',
      blurb: "Only order meals your child has loved before. It's okay if they eat the same thing a few days per week.",
    }
  }
  return {
    label: 'Balanced',
    blurb: "Avoid disliked meals; lean toward favorites when there's a choice.",
  }
}

/**
 * Status label + intent — color + copy for the subscription card chip.
 */
export function statusLabel(status: SubscriptionStatus): { label: string; intent: 'active' | 'paused' | 'inactive' } {
  if (status === 'ENABLED') {
    return { label: 'Active', intent: 'active' }
  }
  if (status === 'PAUSED') {
    return { label: 'Paused', intent: 'paused' }
  }
  if (status === 'CANCELLED') {
    return { label: 'Canceled', intent: 'inactive' }
  }
  return { label: 'Not started', intent: 'inactive' }
}

/**
 * MM/DD for a status badge. Formats in UTC because the lifecycle
 * timestamps ship as midnight-UTC ISO strings — local-timezone
 * formatting would slip the day west of Greenwich. Returns null when
 * the string doesn't parse so callers can fall back to date-free copy.
 */
function formatBadgeDate(iso: string): string | null {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) {
    return null
  }
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', timeZone: 'UTC' })
}

/**
 * Status badge copy for one subscription — the pure helper behind the
 * detail-page badge, the My Family auto-order rows, and the student
 * card. Extends `statusLabel` with the lifecycle nuances the plain
 * status enum can't carry:
 *
 *   - PAUSED + `pauseUntil`  → "Paused until MM/DD" — auto-resumes on
 *     that date (PR #1756).
 *   - PAUSED + null          → "Paused indefinitely" — resumes only
 *     when the parent hits Resume.
 *   - ENABLED + `effectiveTo` → "Active · Ends MM/DD" — a pending
 *     end-of-week cancel; still active today, stops on that date.
 */
export function subscriptionStatusBadge(sub: Pick<FamilySubscriptionWire, 'status' | 'pauseUntil' | 'effectiveTo'>): {
  label: string
  intent: 'active' | 'paused' | 'inactive'
} {
  if (sub.status === 'PAUSED') {
    const until = sub.pauseUntil === null ? null : formatBadgeDate(sub.pauseUntil)
    if (until !== null) {
      return { label: `Paused until ${until}`, intent: 'paused' }
    }
    // An unparseable pauseUntil is not a promise to auto-resume — fall
    // back to plain "Paused" rather than claiming "indefinitely".
    return { label: sub.pauseUntil === null ? 'Paused indefinitely' : 'Paused', intent: 'paused' }
  }
  if (sub.status === 'ENABLED' && sub.effectiveTo !== null) {
    const ends = formatBadgeDate(sub.effectiveTo)
    if (ends !== null) {
      return { label: `Active · Ends ${ends}`, intent: 'active' }
    }
  }
  return statusLabel(sub.status)
}
